import { useState } from "react";
import axios from "axios";
import { Star, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const G      = "var(--k-green)";
const G_DARK = "var(--k-green-hover)";
const G_LIGHT = "var(--k-green-soft)";

const LABELS = {
  1: { text: "Mauvais",    color: "#ef4444" },
  2: { text: "Décevant",   color: "#f97316" },
  3: { text: "Correct",    color: "#f59e0b" },
  4: { text: "Très bien",  color: "#84cc16" },
  5: { text: "Excellent",  color: G         },
};

/* ── Star picker premium ── */
function StarPicker({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  const active = hovered || value;

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            style={{
              background: "none", border: "none", cursor: "pointer", padding: 2,
              transition: "transform 0.15s",
              transform: star <= active ? "scale(1.15)" : "scale(1)",
            }}>
            <Star
              size={28}
              fill={star <= active ? "#f59e0b" : "none"}
              color={star <= active ? "#f59e0b" : "#e5e7eb"}
              strokeWidth={1.5}
              style={{ transition: "fill 0.12s, color 0.12s" }}
            />
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {active > 0 && (
          <motion.span
            key={active}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            style={{ fontSize: 13, fontWeight: 700, color: LABELS[active].color }}>
            {LABELS[active].text}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ReviewForm({
  reservationId,
  reviewId,
  hotelName,
  initialRating  = 0,
  initialComment = "",
  onSuccess,
  mode = "create",
}) {
  const [rating,   setRating]   = useState(initialRating);
  const [comment,  setComment]  = useState(initialComment);
  const [status,   setStatus]   = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [focused,  setFocused]  = useState(false);

  const isEdit = mode === "edit";

  const handleSubmit = async () => {
    if (rating === 0) { setErrorMsg("Sélectionnez une note avant d'envoyer."); return; }
    try {
      setStatus("loading");
      setErrorMsg("");
      if (isEdit) {
        await axios.put(`https://api.komorotel.com/api/reviews/${reviewId}/edit`, { rating, comment }, { withCredentials: true });
      } else {
        await axios.post("https://api.komorotel.com/api/reviews", { reservationId, rating, comment }, { withCredentials: true });
      }
      setStatus("success");
      onSuccess?.({ rating, comment });
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Une erreur est survenue.");
      setStatus("error");
    }
  };

  /* ── Succès ── */
  if (status === "success") return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "28px 24px", textAlign: "center" }}>
      <div style={{ width: 52, height: 52, borderRadius: "50%", background: G_LIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CheckCircle size={24} color={G} />
      </div>
      <p style={{ fontSize: 15, fontWeight: 700, color: "#111827", margin: 0 }}>
        {isEdit ? "Avis modifié !" : "Merci pour votre avis !"}
      </p>
      {!isEdit && (
        <p style={{ fontSize: 13, color: "#9ca3af", margin: 0, maxWidth: 260 }}>
          Il sera visible après validation par notre équipe.
        </p>
      )}
    </motion.div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* En-tête */}
      <div>
        <p style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: "0 0 2px" }}>
          {isEdit ? "Modifier votre avis" : "Laisser un avis"}
        </p>
        {hotelName && <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>{hotelName}</p>}
      </div>

      {/* Stars */}
      <StarPicker value={rating} onChange={setRating} />

      {/* Textarea */}
      <div style={{ position: "relative" }}>
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          maxLength={1000}
          rows={4}
          placeholder="Partagez votre expérience (optionnel)..."
          style={{
            width: "100%", boxSizing: "border-box",
            fontSize: 13, color: "#374151",
            fontFamily: "'DM Sans', system-ui, sans-serif",
            border: `1.5px solid ${focused ? G : "#e5e7eb"}`,
            borderRadius: 12, padding: "12px 14px",
            resize: "none", outline: "none",
            transition: "border-color 0.18s, box-shadow 0.18s",
            boxShadow: focused ? `0 0 0 3px rgb(var(--k-green-rgb)/0.09)` : "none",
            lineHeight: 1.6,
          }}
        />
        <span style={{ position: "absolute", bottom: 10, right: 12, fontSize: 11, color: "#d1d5db" }}>
          {comment.length}/1000
        </span>
      </div>

      {/* Erreur */}
      <AnimatePresence>
        {errorMsg && (
          <motion.p
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ fontSize: 12, color: "#ef4444", margin: 0 }}>
            {errorMsg}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Bouton */}
      <button
        onClick={handleSubmit}
        disabled={status === "loading"}
        style={{
          width: "100%", padding: "12px 0",
          background: status === "loading" ? "#d1d5db" : G,
          color: "#fff", border: "none", borderRadius: 12,
          fontSize: 13, fontWeight: 700, cursor: status === "loading" ? "not-allowed" : "pointer",
          fontFamily: "inherit", transition: "background 0.18s",
          WebkitTapHighlightColor: "transparent",
        }}
        onMouseEnter={e => { if (status !== "loading") e.currentTarget.style.backgroundColor = G_DARK; }}
        onMouseLeave={e => { if (status !== "loading") e.currentTarget.style.backgroundColor = G; }}>
        {status === "loading"
          ? "Envoi en cours..."
          : isEdit ? "Sauvegarder" : "Envoyer mon avis →"}
      </button>
    </div>
  );
}